import React, { useState, useMemo } from "react";
import { Row, Col, Spinner } from "react-bootstrap";
import { Input, Select, Empty } from "antd";

// Layout & Table Components
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import useAdvanceTable from "hooks/useAdvanceTable";
import TablePaginationFooter from "components/common/TablePaginationFooter";

const AppVersionsTable = ({
  appVersions = [],
  columns,
  loading = false,
  searchTerm,    // Received from parent to trigger API
  setSearchTerm, // Received from parent to trigger API
}) => {
  // --- State for Pagination and Filter ---
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [selectedStatus, setSelectedStatus] = useState("ALL");

  // --- Column Styling Wrapper (Fixes the "Big Rows") ---
  const styledColumns = useMemo(() => {
    return columns.map((col) => ({
      ...col,
      meta: {
        ...col.meta,
        headerProps: { 
          ...col.meta?.headerProps, 
          className: `${col.meta?.headerProps?.className || ""} py-2 text-900 fw-bold` 
        },
        cellProps: { 
          ...col.meta?.cellProps, 
          className: `${col.meta?.cellProps?.className || ""} py-2` // Shrinks row height
        }
      }
    }));
  }, [columns]);

  // --- Filter Logic ---
  const filteredData = useMemo(() => {
    const term = searchTerm?.trim().toLowerCase() || "";
    
    return (appVersions || []).filter((item) => {
      const statusLabel = item?.status?.toUpperCase() || "";
      const byStatus = selectedStatus === "ALL" || selectedStatus === statusLabel;

      if (!term) return byStatus;

      const haystack = [item?.versionName, item?.appName, item?.platform, statusLabel]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return byStatus && haystack.includes(term);
    });
  }, [appVersions, searchTerm, selectedStatus]);

  const totalElements = filteredData.length;
  const totalPages = totalElements > 0 ? Math.ceil(totalElements / pageSize) : 0;

  // --- Pagination Logic ---
  const paginatedData = useMemo(() => {
    const start = page * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, page, pageSize]);

  const table = useAdvanceTable({
    data: paginatedData,
    columns: styledColumns, // Use the injected styles
    sortable: true,
    pagination: false,
  });

  return (
    <>
      {/* ================= SEARCH & FILTER ROW (Affiliation Style) ================= */}
      <Row className="mb-2 g-3 align-items-center">
        <Col md={8} lg={9}>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <Input
              prefix={<span style={{ fontSize: 14 }}>🔍</span>}
              placeholder="Search by version or platform..."
              value={searchTerm}
              onChange={(e) => { 
                setSearchTerm(e.target.value); 
                setPage(0); 
              }}
              allowClear
              size="large"
              style={{ borderRadius: 12, height: 30, flex: "1 1 300px", maxWidth: 300 }}
            />
            <Select
              size="large"
              value={selectedStatus}
              onChange={(val) => { setSelectedStatus(val); setPage(0); }}
              options={[
                { label: "🌍 ALL STATUS", value: "ALL" },
                { label: "📄 DRAFT", value: "DRAFT" },
                { label: "✅ ACTIVE", value: "ACTIVE" },
                { label: "🚫 DISABLED", value: "DISABLED" },
              ]}
              style={{ minWidth: 180, height: 28 }}
            />
          </div>
        </Col>
        <Col md={4} lg={3} className="text-end">
          <div style={{ fontSize: 13, color: "#64748b" }}>
            Results: <span className="fw-semibold text-dark">{totalElements}</span> of <span className="fw-semibold text-dark">{appVersions.length}</span>
          </div>
        </Col>
      </Row>

      <AdvanceTableProvider {...table}>
        {loading && totalElements === 0 ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
            <Spinner animation="border" variant="success" />
          </div>
        ) : totalElements === 0 ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
            <Empty description="No app versions found" />
          </div>
        ) : (
          <>
            <AdvanceTable
              headerClassName="bg-light text-nowrap align-middle text-dark"
              rowClassName="align-middle white-space-nowrap"
              tableProps={{
                bordered: true,
                striped: true,
                className: "fs-10 mb-0 overflow-hidden",
              }}
            />
            <div className="mt-3">
              <TablePaginationFooter
                page={page}
                totalPages={totalPages}
                totalElements={totalElements}
                onPrev={page > 0 ? () => setPage(page - 1) : undefined}
                onNext={page < totalPages - 1 ? () => setPage(page + 1) : undefined}
                pageSize={pageSize}
                onPageSizeChange={(size) => { setPageSize(size); setPage(0); }}
                pageSizeOptions={[10, 20, 30, 50, 100]}
              />
            </div>
          </>
        )}
      </AdvanceTableProvider>
    </>
  );
};

export default React.memo(AppVersionsTable);